/**
 * YCC Theme — assets/js/main.js
 *
 * Main JavaScript entry point. Initialises all theme components after the
 * DOM is ready. Keep this file lean; heavy logic should live in dedicated
 * modules inside assets/js/modules/.
 *
 * @package YCC
 */

(function () {
  "use strict";

  // -------------------------------------------------------------------------
  // DOM ready helper
  // -------------------------------------------------------------------------

  document.querySelectorAll(".accordion .header").forEach((btn) => {
    btn.addEventListener("click", () => {
      const panel = btn.nextElementSibling;
      const accItem = btn.parentElement;
      const isActive = accItem.classList.contains("active");
      // close others
      document.querySelectorAll(".accordion .accordion_item").forEach((p) => {
        if (p !== accItem) p.classList.remove("active");
      });
      if (!isActive) {
        accItem.classList.add("active");
      } else {
        accItem.classList.remove("active");
      }
    });
  });

  function ready(fn) {
    if (document.readyState !== "loading") {
      fn();
    } else {
      document.addEventListener("DOMContentLoaded", fn);
    }
  }

  // -------------------------------------------------------------------------
  // Sticky header
  // -------------------------------------------------------------------------

  function initStickyHeader() {
    const header = document.getElementById("masthead");
    if (!header) return;

    const threshold = header.offsetHeight;

    window.addEventListener(
      "scroll",
      function () {
        header.classList.toggle("is-sticky", window.scrollY > threshold);
      },
      { passive: true },
    );
  }

  // -------------------------------------------------------------------------
  // Scroll-to-top button
  // -------------------------------------------------------------------------

  function initBlogFilterChoices() {
    if (typeof window.Choices !== "function") return;

    const selects = document.querySelectorAll(
      ".blog_filters select[name='category']",
    );

    selects.forEach((select) => {
      if (select.dataset.choicesReady === "1") return;

      select.removeAttribute("onchange");

      // Keep ordering and use select text as the chosen label.
      new window.Choices(select, {
        searchEnabled: false,
        shouldSort: false,
        itemSelectText: "",
        allowHTML: false,
      });

      select.addEventListener("change", function () {
        if (select.value) {
          window.location.href = select.value;
        }
      });

      select.dataset.choicesReady = "1";
    });
  }

  function initScrollTop() {
    const btn = document.querySelector(".scroll-top");
    if (!btn) return;

    window.addEventListener(
      "scroll",
      function () {
        btn.classList.toggle("is-visible", window.scrollY > 400);
      },
      { passive: true },
    );

    btn.addEventListener("click", function () {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }

  function initFlexibleTables() {
    const FLEXIBLE_TABLE_SELECTOR = ".wp-block-flexible-table-block-table";
    const FLEXIBLE_TABLE_STYLE_ID = "flexible-table-block-inline-css";
    const FALLBACK_MOBILE_BREAKPOINT = 768;
    const EMPTY_CELL_PLACEHOLDER = "-";
    let mobileTableId = 0;
    let processQueued = false;

    function parseMobileBreakpoint() {
      const styleEl = document.getElementById(FLEXIBLE_TABLE_STYLE_ID);
      if (!styleEl || !styleEl.textContent) return FALLBACK_MOBILE_BREAKPOINT;

      const maxWidthMatch = styleEl.textContent.match(
        /max-width\s*:\s*(\d+(?:\.\d+)?)px/i,
      );

      if (!maxWidthMatch) return FALLBACK_MOBILE_BREAKPOINT;

      const parsed = Number.parseFloat(maxWidthMatch[1]);
      if (Number.isNaN(parsed)) return FALLBACK_MOBILE_BREAKPOINT;

      return parsed;
    }

    function getBlockContainers(rootNode) {
      if (
        !rootNode ||
        !(rootNode instanceof Element || rootNode === document)
      ) {
        return [];
      }

      if (rootNode === document) {
        return Array.from(document.querySelectorAll(FLEXIBLE_TABLE_SELECTOR));
      }

      const blocks = [];
      if (rootNode.matches(FLEXIBLE_TABLE_SELECTOR)) {
        blocks.push(rootNode);
      }

      blocks.push(...rootNode.querySelectorAll(FLEXIBLE_TABLE_SELECTOR));
      return blocks;
    }

    function getTargetTables(block) {
      const directTables = Array.from(block.children).filter(
        (child) => child.tagName === "TABLE",
      );

      if (directTables.length > 0) return directTables;

      const firstTable = block.querySelector("table");
      return firstTable ? [firstTable] : [];
    }

    function applyTableVisibility(sourceTable, mobileTable) {
      const breakpoint = parseMobileBreakpoint();
      const isMobile = window.innerWidth <= breakpoint;

      sourceTable.style.display = isMobile ? "none" : "";
      mobileTable.style.display = isMobile ? "block" : "none";
      sourceTable.setAttribute("aria-hidden", isMobile ? "true" : "false");
      mobileTable.setAttribute("aria-hidden", isMobile ? "false" : "true");
    }

    function createMobileVariantTable(sourceTable) {
      const headerCells = Array.from(
        sourceTable.querySelectorAll("thead tr:first-child th"),
      );
      const bodyRows = Array.from(sourceTable.querySelectorAll("tbody tr"));

      if (headerCells.length === 0 || bodyRows.length === 0) {
        return null;
      }

      const mobileWrapper = document.createElement("div");
      mobileWrapper.className = "ycc-flexible-table-mobile";

      bodyRows.forEach((bodyRow, rowIndex) => {
        const mobileTable = document.createElement("table");
        mobileTable.className = "ycc-flexible-table-mobile__table";
        mobileTable.setAttribute("role", "table");
        mobileTable.setAttribute(
          "aria-label",
          "Responsive table row " + String(rowIndex + 1),
        );

        const mobileTbody = document.createElement("tbody");
        const dataCells = Array.from(bodyRow.children).filter(
          (cell) => cell.tagName === "TD" || cell.tagName === "TH",
        );

        headerCells.forEach((headerCell, columnIndex) => {
          const label = headerCell.textContent
            ? headerCell.textContent.trim()
            : "";
          const sourceCell = dataCells[columnIndex] || null;

          const tr = document.createElement("tr");

          const th = document.createElement("th");
          th.setAttribute("scope", "row");
          th.textContent = label || "Column " + String(columnIndex + 1);

          const td = document.createElement("td");

          if (!sourceCell || !sourceCell.textContent.trim()) {
            td.textContent = EMPTY_CELL_PLACEHOLDER;
          } else {
            td.innerHTML = sourceCell.innerHTML;
          }

          tr.appendChild(th);
          tr.appendChild(td);
          mobileTbody.appendChild(tr);
        });

        mobileTable.appendChild(mobileTbody);
        mobileWrapper.appendChild(mobileTable);
      });

      return mobileWrapper;
    }

    function processTables(rootNode = document) {
      const blocks = getBlockContainers(rootNode);
      if (blocks.length === 0) return;

      blocks.forEach((block) => {
        const tables = getTargetTables(block);

        tables.forEach((sourceTable) => {
          if (sourceTable.dataset.yccFlexibleTableReady === "1") {
            const existingId = sourceTable.dataset.yccFlexibleTableMobileId;
            const existingMobileTable = existingId
              ? document.getElementById(existingId)
              : null;

            if (existingMobileTable) {
              applyTableVisibility(sourceTable, existingMobileTable);
            }

            return;
          }

          const mobileVariant = createMobileVariantTable(sourceTable);
          if (!mobileVariant) return;

          mobileTableId += 1;
          const mobileId = "ycc-flexible-table-mobile-" + String(mobileTableId);
          mobileVariant.id = mobileId;

          sourceTable.dataset.yccFlexibleTableReady = "1";
          sourceTable.dataset.yccFlexibleTableMobileId = mobileId;

          sourceTable.insertAdjacentElement("afterend", mobileVariant);
          applyTableVisibility(sourceTable, mobileVariant);
        });
      });
    }

    function queueTableProcessing() {
      if (processQueued) return;
      processQueued = true;

      window.requestAnimationFrame(function () {
        processQueued = false;
        processTables(document);
      });
    }

    processTables(document);

    window.addEventListener("resize", queueTableProcessing, { passive: true });

    if (!document.body || typeof MutationObserver !== "function") return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== "childList" || mutation.addedNodes.length === 0) {
          continue;
        }

        for (const addedNode of mutation.addedNodes) {
          if (!(addedNode instanceof Element)) continue;
          processTables(addedNode);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // -------------------------------------------------------------------------
  // Initialise
  // -------------------------------------------------------------------------

  ready(function () {
    initStickyHeader();
    initScrollTop();
    initBlogFilterChoices();
    initFlexibleTables();
    //stackableGallery();
  });
})();
